import { useTranslation } from 'react-i18next';
import Card from '../../components/common/Card';
import ReactCalender from '../../components/common/Datepicker';
import Label from '../../components/common/Label';
import SelectSearch from '../../components/common/SelectSearch';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { setToastsError } from '../../redux/actions/common';
import env from '../../configs/env';
import { fetchChangeRequestType, fetchDepartments, fetchStores } from '../../redux/actions/api';
import { createStoreOptions } from '../../utils/helpers/option';
import { clearErrors } from '../../redux/actions/error';
import { ConstantsControllerService, ExportControllerService } from '../../services';
import apiErrorHandler from '../../api/apiErrorHandler';
import { formattedDate, getDateTwoMonthsAgo } from '../../utils/helpers/date';
import { setExportError } from '../../redux/actions/modal';

const ExportChangeRequest = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const storage = useSelector((state) => state.storage);
    const { storeCode, isFreshness, isStore } = storage;

    const constants = useSelector((state) => state.constants);
    const { stores, departments, changeRequestTypes } = constants;

    const DEFAULT_STATE = {
        selectedStoreCode: storeCode || '',
        lines: [],
        classes: [],
        selectedDepartment: null,
        selectedLine: null,
        selectedClass: null,
        changeRequestType: isFreshness ? 'FRESHNESS' : 'COMPETITOR',
        startDateMin: formattedDate(new Date()),
        startDateMax: formattedDate(new Date()),
        approvedAtMin: '',
        approvedAtMax: '',
    };

    const [state, setState] = useState(DEFAULT_STATE);

    const {
        selectedStoreCode,
        lines,
        classes,

        selectedDepartment,
        selectedLine,
        selectedClass,
        changeRequestType,
        startDateMin,
        startDateMax,
        approvedAtMin,
        approvedAtMax,
    } = state;

    // 承認開始日・終了日のmin/max
    const maxDate = new Date();
    const minDate = new Date(getDateTwoMonthsAgo(maxDate)); // 2ヶ月前の日付を取得

    useEffect(() => {
        dispatch(clearErrors());
        dispatch(fetchDepartments());
        dispatch(fetchStores());
        dispatch(fetchChangeRequestType());
    }, []);

    // 部門が変更された場合
    useEffect(() => {
        // ライン名、クラス名をクリア
        setState((prevState) => ({
            ...prevState,

            lines: [],
            selectedLine: null,

            classes: [],
            selectedClass: null,
        }));

        // 部門が選択されている場合のみ
        if (selectedDepartment) {
            (async () => {
                try {
                    // ライン一覧を取得
                    const response = await ConstantsControllerService.getLines([selectedDepartment.value]);
                    const lines = response.map((item) => ({
                        value: item.lineCode,
                        label: item.divisionName,
                    }));
                    handleChangeState('lines', lines);
                } catch (error) {
                    apiErrorHandler(dispatch, error);
                }
            })();
        }
    }, [dispatch, selectedDepartment]);

    // ライン名が変更された場合
    useEffect(() => {
        // クラス名をクリア
        setState((prevState) => ({
            ...prevState,

            classes: [],
            selectedClass: null,
        }));

        // ライン名が選択されている場合のみ
        if (selectedLine) {
            (async () => {
                try {
                    // クラス一覧を取得
                    const response = await ConstantsControllerService.geClasses(selectedLine.value);
                    const classes = response.map((item) => ({
                        value: item.classCode,
                        label: item.divisionName,
                    }));
                    handleChangeState('classes', classes);
                } catch (error) {
                    apiErrorHandler(dispatch, error);
                }
            })();
        }
    }, [dispatch, selectedLine]);

    useEffect(() => {
        if (
            startDateMin > startDateMax ||
            new Date(new Date(startDateMin).setDate(new Date(startDateMin).getDate() + 6)) < new Date(startDateMax)
        ) {
            handleChangeState('startDateMax', '');
            return;
        }
    }, [startDateMin]);

    const getChangeRequestTypes = () => {
        if (changeRequestTypes.length === 0) {
            return [];
        }
        if (isStore) {
            if (isFreshness) {
                return [changeRequestTypes.find((item) => item.value === 'FRESHNESS')];
            } else {
                return [changeRequestTypes.find((item) => item.value === 'COMPETITOR')];
            }
        } else {
            return changeRequestTypes.filter((item) => item.value !== 'INSTRUCTION');
        }
    };

    const handleChangeState = (field, value) => {
        setState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleClearForm = () => {
        setState({
            ...DEFAULT_STATE,
        });
    };

    const handleSubmitForm = () => {
        dispatch(clearErrors());
        if (!startDateMax && !startDateMin) {
            dispatch(setToastsError('売変開始日が未入力です。売変開始日を入力してください。'));
            return;
        }
        if (startDateMin > startDateMax) {
            dispatch(setToastsError('売変開始日の終了日は開始日以降を選択してください。'));
            return;
        }
        if (!(
            new Date(startDateMin) <= new Date(startDateMax) &&
            new Date(startDateMax) <= new Date(new Date(startDateMin).setDate(new Date(startDateMin).getDate() + 6))
        )) {
            dispatch(setToastsError('売変開始日の期間は一週間以内で選択してください。'));
            return;
        }

        // 承認日バリデーション
        const formatApproveAtMin = approvedAtMin ? new Date(approvedAtMin) : null;
        const formatApproveAtMax = approvedAtMax ? new Date(approvedAtMax) : null;
        // 開始日入力時
        if (formatApproveAtMin) {
            if (formatApproveAtMin < minDate) {
                dispatch(setToastsError('承認日の開始日は2ヶ月前以降を選択してください。'));
                return;
            }
            if (formatApproveAtMin > maxDate) {
                dispatch(setToastsError('承認日の開始日は本日以前を選択してください。'));
                return;
            }
        }
        // 終了日入力時
        if (formatApproveAtMax) {
            if (formatApproveAtMax < minDate) {
                dispatch(setToastsError('承認日の終了日は2ヶ月前以降を選択してください。'));
                return;
            }
            if (formatApproveAtMax > maxDate) {
                dispatch(setToastsError('承認日の終了日は本日以前を選択してください。'));
                return;
            }
        }
        // 開始日・終了日入力時
        if (formatApproveAtMin && formatApproveAtMax) {
            if (formatApproveAtMax < formatApproveAtMin) {
                dispatch(setToastsError('承認日の終了日は開始日以降を選択してください。'));
                return;
            }
        }

        if (storeCode === '') {
            dispatch(setToastsError(t('export_summary.error-message')));
        } else {
            handleExportFile();
        }
    };

    const handleExportFile = () => {
        const params = {
            selectedStoreCode,
            changeRequestType,
            startDateMin,
            startDateMax,
            approvedAtMin: approvedAtMin ?? null,
            approvedAtMax: approvedAtMax ?? null,
            selectedDepartment: selectedDepartment?.value ?? null,
            selectedLine: selectedLine?.value ?? null,
            selectedClass: selectedClass?.value ?? null,
        };

        const filteredParams = {};
        for (const key in params) {
            if (!(params[key] === '' || params[key] === null)) {
                filteredParams[key] = params[key];
            }
        }

        // バックエンドからダウンロードするため、formを作成してリクエスト
        const form = document.createElement('form');
        form.action = `${env.BACKEND_BASE_URL}/api/export-change-request`;
        form.method = 'POST';
        form.addEventListener('formdata', (e) => {
            const fd = e.formData;
            for (const key in filteredParams) {
                fd.append(key, filteredParams[key]);
            }
        });
        document.body.append(form);

        ExportControllerService.validateExportChangeRequest(filteredParams)
            .then(() => {
                form.submit();
                handleClearForm();
            })
            .catch((error) => {
                if (error.body?.errors[0]?.message.includes('１度にダウンロードできる件数')) {
                    dispatch(
                        setExportError({
                            isShow: true,
                            title: 'ダウンロードエラー',
                            text01: error.body?.errors[0]?.message,
                        }),
                    );
                } else {
                    apiErrorHandler(dispatch, error);
                }
            })
            .finally(() => {
                form.remove();
            });
    };

    return (
        <div className="export-change-request">
            <div className="okc-inner">
                <Card handleSubmit={handleSubmitForm} handleClear={handleClearForm} isExport={true} toggle={false}>
                    <Label type="column" text={t('update_competitor_master.search_form.ok_store.text')} className="col-lg-2 mb-2 mb-lg-4" />
                    <div className="col-lg-4 pr-lg-3 mb-4">
                        <SelectSearch
                            onChange={(option) => handleChangeState('selectedStoreCode', option.value)}
                            options={createStoreOptions(stores)}
                            value={selectedStoreCode}
                            placeholder={t('price_request.oke_store_name.placeholder')}
                            disabled={isStore}
                        />
                    </div>
                    <Label
                        type="column"
                        text={t('approved_registration_price.search_form.division_name.title')}
                        className="col-lg-2 pl-lg-3 mb-2 mb-lg-4"
                    />
                    <div className="col-lg-4 mb-4">
                        <SelectSearch
                            isClearable={true}
                            options={departments}
                            placeholder={t('prohibited_product_list.search_form.division.placeholder')}
                            onChange={(value) => handleChangeState('selectedDepartment', value)}
                            value={selectedDepartment?.value}
                        />
                    </div>
                    {/* 売変理由 */}
                    <Label type="column" text="売変理由" className="col-lg-2 mb-2 mb-lg-4" />
                    <div className="col-lg-4 mb-4">
                        <SelectSearch
                            placeholder="売変理由を選択してください"
                            options={getChangeRequestTypes()}
                            onChange={(target) => handleChangeState('changeRequestType', target.value)}
                            value={changeRequestType}
                            disabled={isStore}
                        />
                    </div>
                    <Label
                        type="column"
                        text={t('price_registration.search_form.line_name.title')}
                        className="col-lg-2 pl-lg-3 mb-2 mb-lg-4"
                    />
                    <div className="col-lg-4 mb-4">
                        <SelectSearch
                            isClearable={true}
                            options={lines}
                            placeholder={t('price_registration.search_form.line_name.placeholder')}
                            onChange={(value) => handleChangeState('selectedLine', value)}
                            value={selectedLine?.value}
                            disabled={!selectedDepartment || !lines.length}
                        />
                    </div>
                    <Label type="column" className="col-lg-2 mb-2 mb-lg-4" />
                    <div className="col-lg-4 pr-lg-3 mb-4"></div>
                    <Label
                        type="column"
                        text={t('price_registration.search_form.class_name.title')}
                        className="col-lg-2 pl-lg-3 mb-2 mb-lg-4"
                    />
                    <div className="col-lg-4 mb-4">
                        <SelectSearch
                            isClearable={true}
                            options={classes}
                            placeholder={t('price_registration.search_form.class_name.placeholder')}
                            onChange={(value) => handleChangeState('selectedClass', value)}
                            value={selectedClass?.value}
                            disabled={!selectedLine || !classes.length}
                        />
                    </div>
                    <Label className="col-lg-2 mb-2 mb-lg-4" type="column" text="売変開始日" isRequired={true} />
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            classNameInputDateTime="js-datepicker startDateMin"
                            onChange={(value) => handleChangeState('startDateMin', value)}
                            value={startDateMin}
                        />
                    </div>
                    <div className="col-lg-2 justify-content-center mb-4">〜</div>
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            classNameInputDateTime="js-datepicker startDateMax"
                            onChange={(value) => handleChangeState('startDateMax', value)}
                            value={startDateMax}
                            minDate={startDateMin ? new Date(startDateMin) : ''}
                            maxDate={startDateMin ? new Date(new Date(startDateMin).setDate(new Date(startDateMin).getDate() + 6)) : ''}
                        />
                    </div>
                    <Label className="col-lg-2 mb-2 mb-lg-4" type="column" text={t('export_change_request.approve_date.label')} />
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            placeholder={t('export_change_request.approve_date.min_date_placeholder')}
                            onChange={(value) => handleChangeState('approvedAtMin', value)}
                            value={approvedAtMin}
                            minDate={minDate}
                            maxDate={maxDate}
                        />
                    </div>
                    <div className="col-lg-2 justify-content-center mb-4">〜</div>
                    <div className="col-lg-4 mb-4">
                        <ReactCalender
                            placeholder={t('export_change_request.approve_date.max_date_placeholder')}
                            onChange={(value) => handleChangeState('approvedAtMax', value)}
                            value={approvedAtMax}
                            minDate={approvedAtMin ? new Date(approvedAtMin) : minDate}
                            maxDate={maxDate}
                        />
                    </div>
                </Card>
            </div>
        </div>
    );
};
export default ExportChangeRequest;
